/**
 * Configuration Manager API Service
 * Handles API calls to the Configuration Manager backend
 */

// Determine the API base URL
const getApiBaseUrl = () => {
  // Check for environment variable first
  if (process.env.REACT_APP_CONFIG_MANAGER_API) {
    return process.env.REACT_APP_CONFIG_MANAGER_API;
  }
  
  // For local development, determine hostname dynamically
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5001';
    }
    // For network access (e.g., 192.168.x.x)
    return `http://${hostname}:5001`;
  }
  
  return 'http://localhost:5001';
};

const API_BASE_URL = getApiBaseUrl();

class ConfigManagerAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async getAllConfigs(layer = 'backend') {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/all?layer=${layer}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching all configs:', error);
      throw error;
    }
  }

  async getConfig(environment, layer = 'backend') {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}?layer=${layer}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching config for ${environment}:`, error);
      throw error;
    }
  }

  async updateConfig(environment, configData, layer = 'backend') {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}?layer=${layer}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating config for ${environment}:`, error);
      throw error;
    }
  }

  async getPermissions(userId = 'anonymous') {
    try {
      const response = await fetch(`${this.baseUrl}/api/permissions?user_id=${userId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw error;
    }
  }

  async createBackup(environment) {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/backup/${environment}`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error creating backup for ${environment}:`, error);
      throw error;
    }
  }

  async restoreBackup(environment, backupPath) {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/restore/${environment}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ backup_path: backupPath }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error restoring backup for ${environment}:`, error);
      throw error;
    }
  }

  async getFieldMapping() {
    try {
      const response = await fetch(`${this.baseUrl}/api/field-mapping`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching field mapping:', error);
      throw error;
    }
  }

  async getLayerFields(layer) {
    try {
      const response = await fetch(`${this.baseUrl}/api/field-mapping/${layer}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching fields for layer ${layer}:`, error);
      throw error;
    }
  }

  async getFileMapping() {
    try {
      const response = await fetch(`${this.baseUrl}/api/file-mapping`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching file mapping:', error);
      throw error;
    }
  }

  // Session management methods
  async checkLock(environment, layer = 'backend') {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/lock?layer=${layer}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error checking lock for ${environment}:`, error);
      throw error;
    }
  }

  async startSession(environment, layer = 'backend', userId = 'anonymous') {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/start?layer=${layer}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error starting session for ${environment}:`, error);
      throw error;
    }
  }

  async saveDiff(environment, sessionId, field, oldValue, newValue, layer = 'backend') {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/${sessionId}/diff?layer=${layer}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ field, oldValue, newValue }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error saving diff for ${environment}:`, error);
      throw error;
    }
  }

  async saveSession(environment, sessionId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/${sessionId}/save`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error saving session for ${environment}:`, error);
      throw error;
    }
  }

  async commitSession(environment, sessionId, layer = 'backend') {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/${sessionId}/commit?layer=${layer}`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error committing session for ${environment}:`, error);
      throw error;
    }
  }

  async saveAndCommit(environment, sessionId, layer = 'backend') {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/${sessionId}/save-commit?layer=${layer}`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error save-committing session for ${environment}:`, error);
      throw error;
    }
  }

  async cancelSession(environment, sessionId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/${sessionId}/cancel`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error cancelling session for ${environment}:`, error);
      throw error;
    }
  }

  async getSession(environment, sessionId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/${sessionId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching session for ${environment}:`, error);
      throw error;
    }
  }

  async undoChange(environment, sessionId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/${sessionId}/undo`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error undoing change for ${environment}:`, error);
      throw error;
    }
  }

  async redoChange(environment, sessionId) {
    try {
      const response = await fetch(`${this.baseUrl}/api/config/${environment}/session/${sessionId}/redo`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error redoing change for ${environment}:`, error);
      throw error;
    }
  }
}

export default new ConfigManagerAPI();

